/**
 * Create Organization Dialog
 *
 * Dialog for superadmins to create a new organization.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AsyncButton } from '@/components/ui';
import { IconName } from '@/icons';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { Button } from '@/components/ui/button';
import type {
  CreateOrganizationInput,
  CreateOrganizationOutput,
} from '@/types/generated';

interface CreateOrgDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateOrgDialog({ isOpen, onClose }: CreateOrgDialogProps) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { execute, isLoading } = useOrchestrator<
    CreateOrganizationInput,
    CreateOrganizationOutput
  >(Action.CREATE_ORGANIZATION);

  const isValid =
    code.trim().length >= 2 &&
    code.trim().length <= 4 &&
    title.trim().length > 0 &&
    title.trim().length <= 200;

  const handleCreate = async () => {
    if (!isValid) return;
    setError(null);

    const result = await execute({
      code: code.trim().toUpperCase(),
      title: title.trim(),
    });

    if (result.code === ResponseCode.SUCCESS) {
      setCode('');
      setTitle('');
      onClose();
    } else if (result.code === ResponseCode.DUPLICATE_RESOURCE) {
      setError('An organization with this code or title already exists');
    } else if (result.code === ResponseCode.FORBIDDEN) {
      setError('Only superadmin users can create organizations');
    }
  };

  const handleClose = () => {
    setCode('');
    setTitle('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="new-org-code">Organization Code</Label>
            <Input
              id="new-org-code"
              value={code}
              onChange={e => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              maxLength={4}
              className="font-mono"
              placeholder="XXXX"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              2-4 uppercase characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-org-title">Title</Label>
            <Input
              id="new-org-title"
              value={title}
              onChange={e => {
                setTitle(e.target.value);
                setError(null);
              }}
              maxLength={200}
              placeholder="Organization title"
              disabled={isLoading}
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <AsyncButton
            onClick={handleCreate}
            disabled={!isValid || isLoading}
            isLoading={isLoading}
            icon={IconName.PLUS}
            loadingText="Creating..."
          >
            Create
          </AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
